import type { PublicPluginAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import type { ACTION } from '@atlaskit/editor-common/analytics/types/enums';
import {
	ACTION_SUBJECT_ID,
	EVENT_TYPE,
	ACTION_SUBJECT,
} from '@atlaskit/editor-common/analytics/types/enums';
import type { AnalyticsPlugin } from '@atlaskit/editor-plugin-analytics/analyticsPluginType';

import type { EditorOfflineStoragePlugin } from './editorOfflineStoragePluginType.ts';
import type { Status } from './pm-plugins/resync-online.ts';

type BaseAnalyticsEventProps = {
	api: PublicPluginAPI<[EditorOfflineStoragePlugin, AnalyticsPlugin]> | undefined;
};

type ResyncOnlineEventProps = BaseAnalyticsEventProps & {
	action: ACTION.OFFLINE_STORAGE_RESYNC_ONLINE;
	resyncStatus: Status;
	isActiveTab: boolean;
};

type BroadcastChannelUpdateEventProps = BaseAnalyticsEventProps & {
	action: ACTION.OFFLINE_STORAGE_BROADCAST_CHANNEL_FIRST_UPDATE;
};

type TooManyRetriesErrorEventProps = BaseAnalyticsEventProps & {
	action: ACTION.OFFLINE_STORAGE_TOO_MANY_RETRIES_ERROR;
	errorMessage: string;
	errorInfo: string;
	retrySource: string;
	maxRetries: number;
};

type RestoreStepsInitiatedEventProps = BaseAnalyticsEventProps & {
	action: ACTION.OFFLINE_STORAGE_RESTORE_STEPS_INITIATED;
	retryCount: number;
};

type FailedStepsErrorEventProps = BaseAnalyticsEventProps & {
	action: ACTION.OFFLINE_STORAGE_FAILED_STEPS;
	errorInfo: string;
	stepsCount: number;
};

type StepsDatabaseErrorEventProps = BaseAnalyticsEventProps & {
	action: ACTION.OFFLINE_STORAGE_STEPS_DATABASE_ERROR;
	errorMessage: string;
	errorSource: string;
	errorCause: {
		contentIdPresent: boolean;
		stepsDatabaseInitAttempted: boolean;
		isSSR: boolean;
		isIndexedDBAvailable: boolean | undefined;
	};
};

type FailedStoreAction = 'OFFLINE_EDITING_FAILED_STORE_RECOVERY_VERSION';
type FailedStoreOfflineVersion = BaseAnalyticsEventProps & {
	action: FailedStoreAction;
	errorMessage: string;
	manualRetryCount?: number;
};

type TrackStoreAction = 'OFFLINE_EDITING_SUCCESS_STORE_RECOVERY_VERSION';
type TrackStoreOfflineVersion = BaseAnalyticsEventProps & {
	action: TrackStoreAction;
	success: boolean | undefined;
	manualRetryCount?: number;
};

type VersionInsertionErrorFlagShownAction = 'OFFLINE_EDITING_VERSION_INSERTION_ERROR_FLAG_SHOWN';
type VersionInsertionErrorFlagShownEventProps = BaseAnalyticsEventProps & {
	action: VersionInsertionErrorFlagShownAction;
	manualRetryCount: number;
	errorMessage?: string;
};

type VersionInsertionErrorFlagDismissedAction =
	'OFFLINE_EDITING_VERSION_INSERTION_ERROR_FLAG_DISMISSED';
type VersionInsertionErrorFlagDismissedEventProps = BaseAnalyticsEventProps & {
	action: VersionInsertionErrorFlagDismissedAction;
	manualRetryCount: number;
};

type DataExpiryCleanupAction = 'OFFLINE_EDITING_DATA_EXPIRY_CLEANUP';
type DataExpiryCleanupEventProps = BaseAnalyticsEventProps & {
	action: DataExpiryCleanupAction;
	contentId: string;
	daysExpired: number;
	stepsCount: number;
	hasBackup: boolean;
};

type BackgroundCleanupErrorAction = 'OFFLINE_EDITING_BACKGROUND_CLEANUP_ERROR';
type BackgroundCleanupErrorEventProps = BaseAnalyticsEventProps & {
	action: BackgroundCleanupErrorAction;
	errorMessage: string;
	errorSource: 'database_check' | 'cleanup_init';
	contentId?: string;
	totalOfflineDatabases?: number;
};

type BackgroundCleanupCompletedAction = 'OFFLINE_EDITING_BACKGROUND_CLEANUP_COMPLETED';
type BackgroundCleanupDetail = {
	contentId: string;
	deletionReason: 'expired' | 'empty' | 'invalid';
	daysExpired: number;
	stepsCount: number;
	hasBackup: boolean;
};
type BackgroundCleanupCompletedEventProps = BaseAnalyticsEventProps & {
	action: BackgroundCleanupCompletedAction;
	databasesProcessed: number;
	databasesDeleted: number;
	totalOfflineDatabases: number;
	cleanupDetails: BackgroundCleanupDetail[];
};

/**
 * Forces a value to a specific type using TypeScript's 'as' keyword.
 * Usage: const forcedValue = value as DesiredType;
 */
type FireAnalyticsEventProps =
	| TooManyRetriesErrorEventProps
	| StepsDatabaseErrorEventProps
	| ResyncOnlineEventProps
	| BroadcastChannelUpdateEventProps
	| FailedStepsErrorEventProps
	| FailedStoreOfflineVersion
	| TrackStoreOfflineVersion
	| VersionInsertionErrorFlagShownEventProps
	| VersionInsertionErrorFlagDismissedEventProps
	| DataExpiryCleanupEventProps
	| RestoreStepsInitiatedEventProps
	| BackgroundCleanupErrorEventProps
	| BackgroundCleanupCompletedEventProps;

const TRACK_ACTIONS: FireAnalyticsEventProps['action'][] = [
	'OFFLINE_EDITING_SUCCESS_STORE_RECOVERY_VERSION',
	'OFFLINE_EDITING_VERSION_INSERTION_ERROR_FLAG_SHOWN',
];

const isTrackEvent = (props: FireAnalyticsEventProps) => {
	return TRACK_ACTIONS.includes(props.action);
};

type BaseEventProps = {
	actionSubject: ACTION_SUBJECT.OFFLINE_EDITING;
	actionSubjectId: ACTION_SUBJECT_ID.OFFLINE_EDITING;
	eventType: EVENT_TYPE.TRACK | EVENT_TYPE.OPERATIONAL;
	attributes?: {
		[key: string]: unknown;
	};
};

export const fireOfflineStorageAnalyticsEvent = (props: FireAnalyticsEventProps): void => {
	const { api, action, ...otherAttributes } = props;
	if (!api || !api.analytics) {
		return;
	}

	// Get additional context from shared state
	const sharedPluginState = api?.editorOfflineStorage?.sharedState.currentState();
	const { version, steps, status, disabled, contentId } = sharedPluginState ?? {};

	api.analytics.actions?.fireAnalyticsEvent?.<
		Omit<FireAnalyticsEventProps, 'api'> & BaseEventProps,
		'customEventType'
	>({
		eventType: isTrackEvent(props) ? EVENT_TYPE.TRACK : EVENT_TYPE.OPERATIONAL,
		action,
		actionSubject: ACTION_SUBJECT.OFFLINE_EDITING,
		actionSubjectId: ACTION_SUBJECT_ID.OFFLINE_EDITING,
		attributes: {
			// Additonal context.
			version,
			stepsCount: steps?.length,
			status,
			disabled,
			contentId,
			// Action specific attributes.
			...otherAttributes,
		},
	});
};

export const parseErrorMessage = (error: Error | unknown): string => {
	if (!error) {
		return 'Unknown error';
	}
	if (error instanceof Error) {
		return `${error.name}: ${error.message}`;
	}
	return String(error);
};

export const parseErrorMessages = (errors: Error[]): string => {
	return errors.map((e) => parseErrorMessage(e)).join(';');
};
